import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page, useCopilotRecord } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { HierarchyTrail } from '@/components/forge/HierarchyTrail'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Mono, Num } from '@/components/forge/primitives'
import {
  useDeleteShiftHandover,
  useShiftHandover,
  useShiftHandoverAccess,
  useShiftHandoverStream,
  useUpdateShiftHandover,
} from '@/features/shiftHandovers/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatDateTime, formatDay } from '@/lib/format'
import { ShiftHandoverFormDialog } from './ShiftHandoverFormDialog'
import { useHandoverFormOptions } from './useHandoverFormOptions'

/**
 * One handover, read. LIVE — `GET /v1/shifts/handovers/{id}`, kept live by
 * `/shifts/handovers/{id}/stream` for as long as it is open, so somebody else
 * adding a work order to it shows up here without a reload.
 *
 * Its own route, `/app/shift-handovers/:handoverId`, rather than a dialog over
 * the list: a handover is the thing somebody sends the next shift a link to.
 * The list's old `?handover=<id>` still lands here — see the list page.
 *
 * Edit and Delete are offered only to a reader the server would let do them —
 * see `useShiftHandoverAccess`. Delete asks first: it is soft on the server, but
 * no screen can undo it.
 */
export function ShiftHandoverDetailPage() {
  const { t } = useTranslation(['app', 'common'])
  const { handoverId } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const access = useShiftHandoverAccess()
  const detail = useShiftHandover(handoverId, { enabled: access.granted })
  useShiftHandoverStream(handoverId, { enabled: detail.isSuccess })
  // The handover's serial in the assistant's context chip.
  useCopilotRecord('shift_handovers', detail.data?.id, detail.data?.serialNumber)
  const update = useUpdateShiftHandover()
  const remove = useDeleteShiftHandover()
  const options = useHandoverFormOptions({ enabled: access.granted })

  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const handover = detail.data
  const changeable = Boolean(handover) && access.canChange(handover)

  useDocumentTitle(`${handover?.serialNumber ?? t('shiftHandovers.detail.loading')} · ${t('common:brand.name')}`)

  const closeForm = () => {
    setEditing(false)
    update.reset()
  }

  const onDelete = () =>
    remove.mutate(handover.id, {
      onSuccess: () => {
        toast(t('shiftHandovers.toast.deleted', { id: handover.serialNumber }))
        navigate('/app/shift-handovers', { replace: true })
      },
      onError: () => toast(t('shiftHandovers.toast.deleteFailed'), 'error'),
    })

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ForgeButton asChild>
          <Link to="/app/shift-handovers">
            {/* A direction, not a glyph — mirrored under `dir="rtl"`. */}
            <Icon name="right" size="xs" className="rotate-180 rtl:rotate-0" />
            {t('shiftHandovers.detail.back')}
          </Link>
        </ForgeButton>


        <div className="flex-1" />

        {changeable ? (
          confirming ? (
            <>
              <span className="text-body-sm text-on-red">{t('shiftHandovers.detail.confirmDelete')}</span>
              <ForgeButton onClick={() => setConfirming(false)}>{t('common:actions.cancel')}</ForgeButton>
              <ForgeButton variant="danger" disabled={remove.isPending} onClick={onDelete}>
                {remove.isPending ? t('shiftHandovers.detail.deleting') : t('shiftHandovers.detail.deleteConfirm')}
              </ForgeButton>
            </>
          ) : (
            <>
              <ForgeButton variant="ghost" onClick={() => setConfirming(true)}>
                {t('common:actions.delete')}
              </ForgeButton>
              <ForgeAIButton action="updateShiftHandover" recordId={detail.data?.id} recordLabel={detail.data?.serialNumber} />
              <ForgeButton onClick={() => setEditing(true)} disabled={editing}>
                {t('common:actions.edit')}
              </ForgeButton>
            </>
          )
        ) : null}
      </div>

      {access.isPending ? (
        <SkeletonRows count={4} />
      ) : !access.granted ? (
        <EmptyState icon="lock" title={t('shiftHandovers.forbidden')} body={t('shiftHandovers.forbiddenBody')} />
      ) : detail.isPending ? (
        <SkeletonRows count={4} />
      ) : detail.isError ? (
        <ErrorState error={detail.error} title={t('shiftHandovers.detail.loadFailed')} onRetry={() => detail.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          <HandoverHeader handover={handover} isMine={access.isMine(handover)} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Panel className="min-w-0">
              <Eyebrow as="h2" className="mb-2">
                {t('shiftHandovers.detail.notes')}
              </Eyebrow>
              <p className="m-0 text-body whitespace-pre-wrap">{handover.notes}</p>
            </Panel>

            <div className="flex min-w-0 flex-col gap-4">
              <LinkedList
                label={t('shiftHandovers.links.workOrders')}
                items={handover.workOrders ?? []}
                to={(item) => `/app/work-orders?wo=${item.id}`}
                meta={(item) => t(`common:severity.${item.priority}`, { defaultValue: item.priority })}
              />
              <LinkedList
                label={t('shiftHandovers.links.logbookEntries')}
                items={handover.logbookEntries ?? []}
                to={(item) => `/app/logbook/${item.id}`}
                meta={(item) => t(`common:severity.${item.severity}`, { defaultValue: item.severity })}
              />
              <LinkedList
                label={t('shiftHandovers.links.anomalies')}
                items={handover.anomalies ?? []}
                to={(item) => `/app/anomalies/${item.id}`}
                meta={(item) => t(`common:severity.${item.severity}`, { defaultValue: item.severity })}
              />
            </div>
          </div>
        </div>
      )}

      {editing && handover ? (
        <ShiftHandoverFormDialog
          /* Mounted only while open, so it seeds afresh from the detail — which
             `staleTime: 0` and the stream keep current. */
          open
          handover={handover}
          shifts={options.shifts}
          shiftsFailed={options.shiftsFailed}
          canLink={options.canLink}
          pending={update.isPending}
          error={update.error}
          onClose={closeForm}
          onSubmit={(payload) =>
            update.mutate(payload, {
              onSuccess: (saved) => {
                closeForm()
                toast(t('shiftHandovers.toast.updated', { id: saved.serialNumber }))
              },
              /* Field refusals are drawn on the form, which stays open. */
              onError: () => toast(t('shiftHandovers.toast.saveFailed'), 'error'),
            })
          }
        />
      ) : null}
    </Page>
  )
}

/* ══ HEADER ════════════════════════════════════════════════════════════ */

/** The record, stated once — the same anatomy as the log book entry's header. */
function HandoverHeader({ handover, isMine }) {
  const { t } = useTranslation(['app', 'common'])
  /* Not `!==`: an unedited row's two stamps can differ by microseconds, so only
     a gap of more than a minute counts as an edit. */
  const edited =
    Boolean(handover.updatedAt && handover.createdAt) &&
    new Date(handover.updatedAt).getTime() - new Date(handover.createdAt).getTime() > 60_000

  return (
    <Panel className="flex items-start gap-6 max-lg:flex-wrap">
      <div className="min-w-0 flex-1">
        <h1 className="m-0 text-headline">{t('shiftHandovers.detail.title', { id: handover.serialNumber })}</h1>
        <HierarchyTrail hierarchy={handover.hierarchy} className="mt-2" />
      </div>

      <div className="shrink-0 max-lg:w-full">
        <dl className="m-0 flex flex-wrap items-start gap-x-8 gap-y-3">
          <Fact label={t('shiftHandovers.columns.shift')}>
            {handover.shift?.name ?? t('shiftHandovers.unknownShift')}
            {handover.shift && !handover.shift.active ? (
              <span className="ms-1.5 text-ink-dim">{t('shiftHandovers.detail.shiftInactive')}</span>
            ) : null}
          </Fact>

          {/* A calendar day, so `formatDay` — never moved across midnight. */}
          <Fact label={t('shiftHandovers.columns.date')}>
            <Num>{formatDay(handover.shiftDate)}</Num>
          </Fact>

          <Fact label={t('shiftHandovers.columns.created')}>
            <Num>{formatDateTime(handover.createdAt)}</Num>
            {isMine ? <span className="ms-1.5 text-ink-dim">{t('shiftHandovers.byYou')}</span> : null}
            {edited ? (
              <span className="block text-micro text-ink-dim">
                <Num>{t('shiftHandovers.detail.edited', { when: formatDateTime(handover.updatedAt) })}</Num>
              </span>
            ) : null}
          </Fact>
        </dl>
      </div>
    </Panel>
  )
}

function Fact({ label, children }) {
  return (
    <div>
      <Eyebrow as="dt">{label}</Eyebrow>
      <dd className="m-0 mt-1.5 text-body-sm font-medium">{children}</dd>
    </div>
  )
}

/* ══ BODY ══════════════════════════════════════════════════════════════ */

/**
 * One of the three carried lists, each item a link to its own screen. The
 * summaries are as of the last change to the handover — a linked item's own
 * change does not fire this stream (see `useShiftHandoverStream`).
 */
function LinkedList({ label, items, to, meta }) {
  const { t } = useTranslation('app')
  return (
    <Panel>
      <Eyebrow as="h2" className="mb-3">
        {label} <Num className="text-ink-dim">{items.length}</Num>
      </Eyebrow>
      {items.length === 0 ? (
        <p className="m-0 text-body-sm text-ink-dim">{t('shiftHandovers.detail.noneLinked')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {items.map((item) => (
            <li key={item.id} className="flex items-baseline gap-2 text-body-sm">
              <Mono className="shrink-0 font-semibold">{item.serialNumber}</Mono>
              <Link to={to(item)} className="min-w-0 truncate hover:text-on-forge">
                {item.title}
              </Link>
              <span className="ms-auto shrink-0 text-micro text-ink-dim">{meta(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}
